import { useState, useCallback } from 'react';
import type { FormSubmissionRequest, FormSubmissionResponse } from '@qdf/shared';
import { submitForm } from '../api/submissions.js';
import { ApiError } from '../api/client.js';

interface UseFormSubmissionReturn {
  submit: (data: FormSubmissionRequest, fileRefs: never[]) => Promise<FormSubmissionResponse>;
  isSubmitting: boolean;
  error: string | null;
  fieldErrors: Record<string, string[]> | null;
}

export function useFormSubmission(): UseFormSubmissionReturn {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string[]> | null>(null);

  const submit = useCallback(
    async (data: FormSubmissionRequest): Promise<FormSubmissionResponse> => {
      setIsSubmitting(true);
      setError(null);
      setFieldErrors(null);

      try {
        return await submitForm(data);
      } catch (err) {
        if (err instanceof ApiError) {
          setError(err.body.message);
          if (err.body.details) {
            setFieldErrors(err.body.details);
          }
        } else {
          setError('An unexpected error occurred. Please try again.');
        }
        throw err;
      } finally {
        setIsSubmitting(false);
      }
    },
    [],
  );

  return { submit, isSubmitting, error, fieldErrors };
}
